import { Component, OnInit } from "@angular/core";
import moment from "moment";
import { untilDestroyed, UntilDestroy } from "@ngneat/until-destroy";
import { SsoAccountFacade, TimeHelper, SsoAccountData, PendingAgreementType } from "common";
import { Observable, combineLatest } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { DashboardFacade } from "./dashboard.facade";
import { DashboardDataService } from "./services/dashboard-data.service";
import { DashboardData } from "./models/dashboard.model";
import { EntityFacade } from "../entity/domain/+state/entity.facade";
import { VendorOfferData } from "../vendor-offer/domain/models/vendor-offer.model";
import { VendorOfferFacade } from "../vendor-offer/domain/+state/vendor-offer.facade";
import { AgreementStatus, Entity } from "../entity/domain/models/entity.model";

@UntilDestroy()
@Component({
    selector: "ifm-dashboard",
    templateUrl: "./dashboard.component.html",
    styleUrls: ["./dashboard.component.scss"],
    providers: [
        DashboardFacade,
        DashboardDataService,
    ],
    standalone: false
})
export class DashboardComponent implements OnInit {
  readonly AgreementStatus = AgreementStatus;

  greeting: string;
  currentSsoAccount: SsoAccountData;
  vendorOffers: VendorOfferData[];
  data$: Observable<DashboardData>;
  showCompleteApplicationBanner$: Observable<boolean>;
  entity: Entity; // TODO[NP] tems - to remove/refactor (advisor need data from entity, should get some more general data)
  pendingMsaApplicationId: number;

  // Single shared current-account stream — consumed by both the greeting subscription
  // and the banner check, so `accounts/current` is fetched only once.
  private ssoAccount$: Observable<SsoAccountData>;

  constructor(
    private readonly ssoAccountFacade: SsoAccountFacade,
    private readonly dashboardFacade: DashboardFacade,
    private readonly vendorOfferFacade: VendorOfferFacade,
    private readonly entityFacade: EntityFacade
  ) { }

  ngOnInit(): void {
    this.ssoAccount$ = this.ssoAccountFacade
      .getCurrentSsoAccount$()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.initCurrentSsoAccount();
    this.initData();
    this.initVendorOffers();
    this.initEntity();
  }

  private initCurrentSsoAccount(): void {
    this.ssoAccount$
      .pipe(untilDestroyed(this))
      .subscribe((currentSsoAccount) => {
        this.currentSsoAccount = currentSsoAccount;
        this.greeting = `${TimeHelper.getGreetingTime(moment())}, ${currentSsoAccount?.firstName
          }!`;

        if (currentSsoAccount.nextAgreementToSign?.type === PendingAgreementType.Application) {
          this.pendingMsaApplicationId = currentSsoAccount.nextAgreementToSign.id;
        }
      });
  }

  private initData(): void {
    this.data$ = this.dashboardFacade
      .getDashboardData$()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.showCompleteApplicationBanner$ = combineLatest([
      this.ssoAccount$,
      this.data$,
    ]).pipe(
      map(([ssoAccount, data]) => this.canShowCompleteApplicationBanner(ssoAccount, data))
    );
    this.dashboardFacade.initActions();
  }

  // The "Review and accept your MSA" card is the single CTA for signing the MSA, so
  // suppress the redundant "complete application" banner whenever that card is shown
  // (i.e. any entity still has an unsigned MSA).
  private canShowCompleteApplicationBanner(ssoAccount: SsoAccountData, data: DashboardData): boolean {
    const hasPendingApplicationAgreement =
      ssoAccount?.nextAgreementToSign?.type === PendingAgreementType.Application;
    const hasAgreementToReview = (data?.entitiesData ?? []).some(
      (entityData) => entityData.entity?.msaAgreementInfo?.status !== AgreementStatus.Signed
    );
    return hasPendingApplicationAgreement && !hasAgreementToReview;
  }

  private initVendorOffers(): void {
    this.vendorOfferFacade
      .getVendorOffers()
      .pipe(untilDestroyed(this))
      .subscribe((vendorOffers) => {
        this.vendorOffers = vendorOffers;
      });
  }

  // TODO[NP] tems - to remove/refactor (advisor need data from entity, should get some more general data)
  private initEntity(): void {
    this.entityFacade
      .getEntity$(null)
      .pipe(untilDestroyed(this))
      .subscribe((entity) => {
        this.entity = entity;
      });
  }
}
